import React, { ReactElement, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { ArrowUpDownIcon } from 'lucide-react';
import { DropdownMenuState } from 'src/components/GlobalPageV2/DropdownMenu/DropdownMenu.state';
import { MenuWrapper, SubMenuItemWrapper } from 'src/domains/common/components/menu-bar/menu-bar';
import { observer } from 'src/utils/mobx-react';
import { MARKERS_OF_HARM_POPUP_SORT_OPTIONS, MarkersOfHarmPopupSortType } from './MarkersOfHarmPopupTab.types';

type MarkersOfHarmPopupSortMenuPropsType = {
    sortBy: MarkersOfHarmPopupSortType;
    onSortChange: (sortBy: MarkersOfHarmPopupSortType) => void;
};

export const MarkersOfHarmPopupSortMenu = observer(
    'MarkersOfHarmPopupSortMenu',
    ({ sortBy, onSortChange }: MarkersOfHarmPopupSortMenuPropsType): ReactElement => {
        const [dropdownState] = useState(() => new DropdownMenuState());
        const menuAnchorRef = useRef<HTMLDivElement | null>(null);
        const menuPanelRef = useRef<HTMLDivElement | null>(null);
        const [menuPosition, setMenuPosition] = useState<{ top: number; right: number } | null>(null);

        useEffect(() => {
            if (!dropdownState.isOpen) {
                return;
            }

            const handleClickOutside = (event: MouseEvent): void => {
                const target = event.target;
                if (!(target instanceof Node)) {
                    return;
                }

                if (menuAnchorRef.current?.contains(target) === true) {
                    return;
                }

                if (menuPanelRef.current?.contains(target) === true) {
                    return;
                }

                dropdownState.closeDropdownMenu();
            };

            document.addEventListener('mousedown', handleClickOutside);
            return () => {
                document.removeEventListener('mousedown', handleClickOutside);
            };
        }, [dropdownState, dropdownState.isOpen]);

        const handleToggleMenu = (): void => {
            if (!dropdownState.isOpen && menuAnchorRef.current !== null) {
                const rect = menuAnchorRef.current.getBoundingClientRect();
                setMenuPosition({
                    top: rect.bottom + 4,
                    right: window.innerWidth - rect.right,
                });
            }
            dropdownState.toggleDropdownMenu();
        };

        const handleSortChange = (nextSortBy: MarkersOfHarmPopupSortType): void => {
            onSortChange(nextSortBy);
            dropdownState.closeDropdownMenu();
        };

        return (
            <div ref={menuAnchorRef}>
                <button
                    type='button'
                    className='inline-flex h-9 cursor-pointer items-center gap-2 rounded-md border border-solid border-border bg-background px-3 py-2 text-sm font-medium text-foreground'
                    data-test='markers-of-harm-popup-sort-trigger'
                    onClick={handleToggleMenu}
                    aria-label='Sort Pop-Ups'
                >
                    Sort by
                    <ArrowUpDownIcon className='size-4 text-muted-foreground' />
                </button>

                {dropdownState.isOpen && menuPosition !== null
                    ? ReactDOM.createPortal(
                          <div
                              ref={menuPanelRef}
                              className='fixed z-50'
                              style={{ top: menuPosition.top, right: menuPosition.right }}
                          >
                              <MenuWrapper
                                  className='min-w-56 p-1'
                                  data-test='markers-of-harm-popup-sort-menu'
                              >
                                  {MARKERS_OF_HARM_POPUP_SORT_OPTIONS.map((option) => (
                                      <SubMenuItemWrapper
                                          key={option.id}
                                          label={option.label}
                                          isSelected={sortBy === option.id}
                                          onClick={(): void => handleSortChange(option.id)}
                                          data-test={`markers-of-harm-popup-sort-option-${option.id}`}
                                      />
                                  ))}
                              </MenuWrapper>
                          </div>,
                          document.body
                      )
                    : null}
            </div>
        );
    }
);
